import {match} from 'ts-pattern';

import {useReducer, useCallback, useMemo} from 'react';

import {DocumentPreviewBasePage} from '../types/DocumentPreviewBasePage';
import {DocumentPreviewPage} from '../types/DocumentPreviewPage';
import {DocumentPreviewPageValidationStatus} from '../types/DocumentPreviewPageValidationStatus';
import {DocumentPreviewState} from '../types/DocumentPreviewState';
import {UseDocumentPreviewReturnType} from '../types/UseDocumentPreviewReturnType';

type DocumentPreviewAction =
  | {type: 'SET_ACTIVE_INDEX'; payload: number}
  | {type: 'UPDATE_PAGE'; payload: {pageNumber: number; updates: Partial<DocumentPreviewPage>}}
  | {type: 'TOGGLE_COLLAPSED'}
  | {type: 'SET_PAGES'; payload: DocumentPreviewBasePage[]};

const initializeDocumentPage = (basePage: DocumentPreviewBasePage): DocumentPreviewPage => ({
  ...basePage,
  validationStatus: 'default',
});

const INITIAL_STATE: DocumentPreviewState = {
  pages: [],
  activeIndex: 0,
  isCollapsed: false,
};

const reducer = (
  state: DocumentPreviewState,
  action: DocumentPreviewAction
): DocumentPreviewState =>
  match(action)
    .with({type: 'SET_ACTIVE_INDEX'}, ({payload}) => ({...state, activeIndex: payload}))
    .with({type: 'UPDATE_PAGE'}, ({payload}) => ({
      ...state,
      pages: state.pages.map((page) =>
        page.pageNumber === payload.pageNumber ? {...page, ...payload.updates} : page
      ),
    }))
    .with({type: 'TOGGLE_COLLAPSED'}, () => ({
      ...state,
      isCollapsed: !state.isCollapsed,
    }))
    .with({type: 'SET_PAGES'}, ({payload}) => {
      const hasPages = payload.length > 0;
      const lastPageIndex = payload.length - 1;
      const safeActiveIndex = hasPages
        ? Math.min(Math.max(state.activeIndex, 0), lastPageIndex)
        : 0;

      return {
        ...state,
        pages: payload.map(initializeDocumentPage),
        activeIndex: safeActiveIndex,
      };
    })
    .otherwise(() => state);

export const useDocumentPreview = (): UseDocumentPreviewReturnType => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const setActiveIndex = useCallback((index: number) => {
    dispatch({type: 'SET_ACTIVE_INDEX', payload: index});
  }, []);

  const setPageValidation = useCallback(
    (pageNumber: number, status: DocumentPreviewPageValidationStatus) => {
      dispatch({type: 'UPDATE_PAGE', payload: {pageNumber, updates: {validationStatus: status}}});
    },
    []
  );

  const toggleCollapsed = useCallback(() => {
    dispatch({type: 'TOGGLE_COLLAPSED'});
  }, []);

  const setPages = useCallback((newPages: DocumentPreviewBasePage[]) => {
    dispatch({type: 'SET_PAGES', payload: newPages});
  }, []);

  const controls = useMemo(
    () => ({
      setPages,
      setActiveIndex,
      setPageValidation,
      toggleCollapsed,
    }),
    [setPages, setActiveIndex, setPageValidation, toggleCollapsed]
  );

  return useMemo(
    () => ({
      state,
      controls,
    }),
    [state, controls]
  );
};
